import { azureMapsApiKey as azMapsApiKey } from '../../../env-var.js'
import { helpers, theme } from '@/plugins/theme'
import * as atlas from 'azure-maps-control'
import colors from 'vuetify/lib/util/colors'
import store from '@/store'
import { SupportedReceiverCountries } from '@/utils/constants.js'

export const defaultCoordinates = {
  latitude: 36.099,
  longitude: -80.2494
}

export const mapResizeTimeout = 250

export const addMapControlScript = () => { // TODO MK: Rip this out when we get version of azmaps w/ built in scale bar
  // if (!document.getElementById('atlasMapControlImport')) {
  //   const atlasScript = document.createElement('script')
  //   atlasScript.setAttribute('src', 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/2/atlas.min.js')
  //   atlasScript.setAttribute('id', 'atlasMapControlImport')
  //   document.head.insertBefore(atlasScript, document.head.firstChild)
  // }
}

export const removeMapControlScript = () => { // TODO MK: Rip this out when we get version of azmaps w/ built in scale bar
  // if (document.getElementById('atlasMapControlImport')) document.getElementById('atlasMapControlImport').remove()
  if (document.getElementById('stylesForScaleBar')) document.getElementById('stylesForScaleBar').remove() // This element is produced by the scale bar module
}

const LOCAL_AZMAPS_KEY = 'FMG4SRqJhWH0ix1NBc7lvPLIKxOtE_qrNpFbo078fgg'

export const azureMapsApiKey = () => {
  return azMapsApiKey.includes('VUE') ? LOCAL_AZMAPS_KEY : azMapsApiKey
}

const MAP_CAMERA_PADDING = 80
export function zoomToFeature (feature, map, options = null) {
  if (!feature) return
  const bbox = atlas.data.BoundingBox.fromData(feature)
  if (!Array.isArray(bbox) || bbox.length < 4 || !bbox.slice(0, 4).every(Number.isFinite)) return
  // Out-of-range coordinates (e.g. a swapped lat/long awaiting correction in the EUDR shape editor)
  // would fling the camera off the world — clamp the box to valid WGS84 bounds instead.
  const clamp = (value, limit) => Math.min(Math.max(value, -limit), limit)
  map.setCamera({
    bounds: [clamp(bbox[0], 180), clamp(bbox[1], 90), clamp(bbox[2], 180), clamp(bbox[3], 90)],
    padding: MAP_CAMERA_PADDING,
    type: 'fly',
    ...options
  })
}

// dispose() leaves the autoResize window listener pinning the map; after dispose, setStyle does nothing but remove it
export function disposeMap (map) {
  map.dispose()
  map.setStyle({ autoResize: false })
}

// Polygon Styling
const dodgerBlue = '#1E90FF' // Built-in CSS color
const white = '#FFFFFF'

export const PolygonStyle = {
  Cut: {
    id: 0,
    fill: dodgerBlue,
    fillOpacity: 0.25,
    stroke: white,
    strokeOpacity: 1,

    legendLabel: 'tractCut',
    legendFillOpacity: 0.50,
    legendStrokeOpacity: 1
  },

  Parcels: {
    id: 1,
    fill: white,
    fillOpacity: 0.1,
    stroke: white,
    strokeOpacity: 0.60,
    strokeDashArray: [3, 3],

    legendLabel: 'parcel',
    legendFillOpacity: 0.25,
    legendStrokeOpacity: 0.60
  },

  PickupRadius: {
    id: 2,
    fill: theme.primary,
    fillOpacity: 0.15,
    stroke: theme.primary,
    strokeOpacity: 1,
    strokeDashArray: [4, 4],

    legendLabel: 'pickupRadius',
    legendFillOpacity: 0.25,
    legendStrokeOpacity: 1
  },

  NearbyParcels: {
    id: 3,
    fill: colors.yellow.accent3,
    fillOpacity: 0.1,
    stroke: colors.yellow.accent3,
    strokeOpacity: 0.75,
    strokeDashArray: [1, 3],

    legendLabel: 'nearbyParcels',
    legendFillOpacity: 0,
    legendStrokeOpacity: 1
  },

  FocusedParcel: {
    id: 4,
    fill: colors.yellow.accent2,
    fillOpacity: 0.2,
    stroke: colors.yellow.accent2,
    strokeOpacity: 1,

    legendLabel: 'focusedParcel',
    legendFillOpacity: 0.1,
    legendStrokeOpacity: 1
  },

  SpeciesLayer: {
    id: 5,
    fill: colors.teal.accent3,
    fillOpacity: 0.5,
    stroke: colors.teal.darken2,
    strokeOpacity: 0.9,

    legendLabel: 'speciesLayer',
    legendFillOpacity: 0.35,
    legendStrokeOpacity: 0.9
  },

  SoilOperability: {
    id: 6,
    fill: white,
    fillOpacity: 0.5,
    stroke: white,
    strokeOpacity: 0.8,

    legendLabel: 'operability',
    legendFillOpacity: 0.5,
    legendStrokeOpacity: 0.8
  },

  CustomLayer: {
    id: 7,
    fill: white,
    fillOpacity: 0.35,
    stroke: white,
    strokeOpacity: 0.9,

    legendLabel: 'customLayers',
    legendFillOpacity: 0.35,
    legendStrokeOpacity: 0.9
  },

  NearbyTractBoundaries: {
    id: 8,
    fill: dodgerBlue,
    fillOpacity: 0.25,
    stroke: white,
    strokeOpacity: 1,

    legendLabel: 'nearbyBoundaries',
    legendFillOpacity: 0.50,
    legendStrokeOpacity: 1
  }
}

export function styleFromPolygonType (polygonType) {
  return {
    border: `2px ${polygonType.strokeDashArray ? 'dashed' : 'solid'} ${helpers.getTranslucent(polygonType.legendStrokeOpacity * 100, polygonType.stroke)}`,
    color: helpers.getTranslucent(polygonType.legendFillOpacity * 100, polygonType.fill)
  }
}

export const strokeWidth = 2
export const expressionPolygonOrMultipolygon = ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']]

export function createPolygonLayersForDataSource (dataSource, polygonStyle, sourceLayer = '') {
  return [
    createFillLayerForDataSource(dataSource, polygonStyle, sourceLayer),
    createLineLayerForDataSource(dataSource, polygonStyle, sourceLayer)
  ]
}

function createLineLayerForDataSource (dataSource, polygonStyle, sourceLayer = '') {
  const layer = new atlas.layer.LineLayer(dataSource, null, getLineLayerOptions(polygonStyle, sourceLayer))
  layer.metadata = { type: 'line' }
  return layer
}

function getLineLayerOptions (polygonStyle, sourceLayer = '') {
  return {
    strokeColor: polygonStyle.stroke,
    strokeWidth: strokeWidth,
    strokeOpacity: polygonStyle.strokeOpacity,
    strokeDashArray: polygonStyle.strokeDashArray,
    filter: expressionPolygonOrMultipolygon,
    sourceLayer
  }
}

function createFillLayerForDataSource (dataSource, polygonStyle, sourceLayer = '') {
  const layer = new atlas.layer.PolygonLayer(dataSource, null, getFillLayerOptions(polygonStyle, sourceLayer))
  layer.metadata = { type: 'fill' }
  return layer
}

function getFillLayerOptions (polygonStyle, sourceLayer = '') {
  return {
    fillColor: polygonStyle.fill,
    fillOpacity: polygonStyle.fillOpacity,
    filter: expressionPolygonOrMultipolygon,
    sourceLayer
  }
}

export function coordinatesInBounds (latitude, longitude) {
  return !isNaN(latitude) && !isNaN(longitude) &&
    latitude >= -90 && latitude <= 90 &&
    longitude >= -180 && longitude <= 180
}

export async function validateCoordinatesForReceiverLocation (coordinates) {
  let isValid = false
  try {
    const location = await store.dispatch('tract/getLocationWithCoordinate', coordinates)
    isValid = SupportedReceiverCountries.some(c => c.countryCode === location.countryCode)
  } catch (e) {
    console.warn(e)
  }
  return isValid
}
